/**
 * The orders module's URL adapter (docs/audits/ROLLOUT-ORDERS-SHELL.md §2.1), on the shared
 * factory (layout/useRecordRoute.js, §3 D12).
 *
 *   /orders              the list
 *   /orders/:id          the list with that order's record beside it (O1b, the split); the list
 *                        query rides along, so the list keeps its view, q, sort and page
 *   /orders/:id/amend    the amendment editor for that order (still the existing modal; O5)
 *   /orders/new          not a route: orders come from quotes. Normalised to /orders by replace
 *
 * List keys (defaults omitted): view (D1, default all), q, sort / dir (created · number · total ·
 * customer · delivery; default created desc, D10), page. A sort's dir is omitted only when it is
 * that sort's own default (ORDER_SORT_DEFAULT_DIR: asc for number and customer, desc for the
 * rest; the user's default dir for the user's default sort) and written otherwise, so a bare
 * ?sort=number reads ascending and ?sort=number&dir=desc survives a reload (lap 2026-10-20). Every list request carries them (O1a's
 * contract). View and page push; q, sort and dir replace; a change to any but page starts on
 * page 1.
 *
 * Legacy inbound forms, normalised once by replace (§2.1): ?filter=<s> (the Dashboard's quick
 * actions) and ?status=<s> (hand-typed) become the view that contains s; an explicit view wins,
 * and a value that names no status is dropped.
 *
 * The list defaults (O4, D10) are the user's: orders.list.defaultView / sort / dir in their
 * preferences document (§5.1), over the built-in ones, key by key. They apply only where the URL
 * omits the key and stay out of the URL (a bare /orders is the user's own default view; any
 * other view, All included when it is not the default, is explicit), as Customers' C4b does.
 */
import { useCallback, useMemo } from 'react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { createRecordRoute, positiveInt } from '../layout/useRecordRoute';
import { canonicalOrderStatus } from './orderStatusMap';

export const ORDERS_ROOT = '/orders';

/**
 * The server's views (backend utils/orderViews.js, O1a). The menu shows a subset (D1: All, Open,
 * Completed, Cancelled); the others still parse and fetch, so a link to one works.
 */
export const ORDER_VIEWS = ['all', 'open', 'awaiting_delivery', 'awaiting_payment', 'completed', 'cancelled'];

/** The sortable columns (O1a's whitelist) and each one's first direction. */
export const ORDER_SORTS = ['created', 'number', 'total', 'customer', 'delivery'];
const DIRS = ['asc', 'desc'];
export const ORDER_SORT_DEFAULT_DIR = Object.freeze({
  created: 'desc', number: 'asc', total: 'desc', customer: 'asc', delivery: 'desc'
});

const LIST_KEYS = ['view', 'q', 'page', 'sort', 'dir'];

export const ORDER_LIST_DEFAULTS = Object.freeze({
  view: 'all', q: '', page: 1, sort: 'created', dir: 'desc'
});

/**
 * The list defaults for an orders document: list.defaultView, sort and dir over the built-in
 * ones, key by key (a value the route does not know keeps the built-in one).
 */
export function orderListDefaultsFrom(doc) {
  const list = (doc && doc.list) || {};
  const d = { ...ORDER_LIST_DEFAULTS };
  if (ORDER_VIEWS.includes(list.defaultView)) d.view = list.defaultView;
  if (ORDER_SORTS.includes(list.sort)) d.sort = list.sort;
  if (DIRS.includes(list.dir)) d.dir = list.dir;
  return Object.freeze(d);
}

/** This user's list defaults (O4). */
export function useOrderListDefaults() {
  const { doc } = usePreferences('orders');
  return useMemo(() => orderListDefaultsFrom(doc), [doc]);
}

const PUSH_KEYS = ['view', 'page'];
const FILTER_KEYS = ['view', 'q', 'sort', 'dir'];

/** The aliases that name a status. */
const STATUS_ALIASES = ['filter', 'status'];

/** The canonical state -> the view that contains it (orderViews.js' status predicates). */
const VIEW_FOR_STATE = Object.freeze({
  pending: 'open',
  confirmed: 'open',
  processing: 'open',
  ready: 'awaiting_delivery',
  out_for_delivery: 'awaiting_delivery',
  delivered: 'completed',
  cancelled: 'cancelled'
});

/** The view a stored or legacy status value lands on, or null. */
export const orderViewForStatus = (status) => VIEW_FOR_STATE[canonicalOrderStatus(status)] || null;

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

/**
 * The dir a sort reads when the URL has none: the user's default dir for the default sort,
 * otherwise that column's own first direction.
 */
export function orderDefaultDir(sort, defaults = ORDER_LIST_DEFAULTS) {
  const d = defaults || ORDER_LIST_DEFAULTS;
  if (sort === d.sort) return d.dir;
  return ORDER_SORT_DEFAULT_DIR[sort] || d.dir;
}

/**
 * Legacy inbound forms, normalised once. Returns { pathname, search }, or null when the URL is
 * already canonical.
 */
export function legacyOrderRedirect(pathname, search) {
  const path = String(pathname || '').replace(/\/+$/, '');
  if (path === `${ORDERS_ROOT}/new`) {
    return { pathname: ORDERS_ROOT, search: search || '' };
  }
  if (path !== ORDERS_ROOT) return null;

  const sp = new URLSearchParams(search || '');
  if (!STATUS_ALIASES.some((key) => sp.has(key))) return null;
  let view = null;
  for (const key of STATUS_ALIASES) {
    if (!sp.has(key)) continue;
    view = view || orderViewForStatus(sp.get(key));
    sp.delete(key);
  }
  if (view && !sp.has('view') && view !== ORDER_LIST_DEFAULTS.view) sp.set('view', view);
  const qs = sp.toString();
  return { pathname: ORDERS_ROOT, search: qs ? `?${qs}` : '' };
}

const orderRoute = createRecordRoute({
  root: ORDERS_ROOT,
  listKeys: LIST_KEYS,
  defaults: ORDER_LIST_DEFAULTS,
  useDefaults: useOrderListDefaults,
  pushKeys: PUSH_KEYS,
  filterKeys: FILTER_KEYS,
  subRoutes: { amend: 'amend' },
  newMode: null,
  legacyRedirect: legacyOrderRedirect,
  defaultFor: (key, sp, d) => (key === 'dir'
    ? orderDefaultDir(oneOf(sp.get('sort'), ORDER_SORTS, d.sort), d)
    : d[key]),
  parseList: (sp, d) => {
    const sort = oneOf(sp.get('sort'), ORDER_SORTS, d.sort);
    return {
      view: oneOf(sp.get('view'), ORDER_VIEWS, d.view),
      q: sp.get('q') || '',
      page: positiveInt(sp.get('page')) || 1,
      sort,
      dir: oneOf(sp.get('dir'), DIRS, orderDefaultDir(sort, d))
    };
  }
});

/**
 * Pure parse of an orders location: { mode, id, invalidId, problem, list }.
 * @param {object} [defaults] the list defaults (orderListDefaultsFrom); the built-in ones if omitted
 */
export const parseOrderLocation = (pathname, search, defaults = ORDER_LIST_DEFAULTS) =>
  orderRoute.parse(pathname, search, defaults);

/** Keys a view link carries over from the list on screen: how it is searched and sorted. */
const VIEW_KEEPS = ['q', 'sort', 'dir'];

/**
 * The list URL for a view (O3, the views menu). On the orders list (or a record beside it) it
 * keeps q, sort and dir, so the count beside the view is what the view shows, and starts on
 * page 1; anywhere else it is the bare view. The user's default view is the bare /orders (D10,
 * O4: `all` when nothing is stored), so every other view, All included, is explicit.
 */
export function orderViewHref(view, { pathname = '', search = '' } = {}, defaults = ORDER_LIST_DEFAULTS) {
  const sp = new URLSearchParams();
  const path = String(pathname || '');
  if (path === ORDERS_ROOT || path.startsWith(`${ORDERS_ROOT}/`)) {
    const current = new URLSearchParams(search || '');
    for (const key of VIEW_KEEPS) {
      const value = current.get(key);
      if (value) sp.set(key, value);
    }
  }
  if (view && view !== (defaults || ORDER_LIST_DEFAULTS).view && ORDER_VIEWS.includes(view)) sp.set('view', view);
  const qs = sp.toString();
  return `${ORDERS_ROOT}${qs ? `?${qs}` : ''}`;
}

/**
 * @returns the parse, `defaults` (the user's), and the writers openRecord, closeRecord,
 *   amendRecord, setList.
 */
export default function useOrderRoute() {
  const route = orderRoute.useRecordRoute();
  const { openSubRoute } = route;
  const amendRecord = useCallback((id) => openSubRoute(id, 'amend'), [openSubRoute]);
  return useMemo(() => {
    const { openSubRoute: _sub, openNew: _new, ...rest } = route;
    return { ...rest, amendRecord };
  }, [route, amendRecord]);
}
